import { updateCustomerById } from "../../../../app/api/updateCustomerById";
import { DEFAULT_SEATING_TYPE } from "../../../../Components/Common/Inputs/SeatingInput";
import { BaseAction } from "../../../../core/actions/BaseAction";
import { getTranslatedMessage } from "../../../../Lib/Utils/i18n";
import { isEmpty } from "../../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IUserResponse } from "../../../../Orchestrator/Types/Index";
import { showNotification } from "../../../../Services/notificationService";
import { invalidateUserCaches } from "../invalidateUserCaches";


interface ISavePersonalizeExperienceParams {
  userData: IUserResponse;
  railPreferences: Partial<IUserResponse["railPreferences"]>;
}

class SavePersonalizeExperienceAction extends BaseAction<void, ISavePersonalizeExperienceParams> {
  override async run(): Promise<void> {
    const { userData, railPreferences } = this.params || {};

    try {
      if (!userData?.id) {
        console.error("User data not found.");
        return;
      }

      if (!railPreferences) {
        console.error("Rail preferences data not provided.");
        return;
      }
      let payload: Partial<IUserResponse["railPreferences"]> = {
        ...(userData?.railPreferences || {}),
      }

      if ("preferredOrigin" in railPreferences) {
        payload.preferredOrigin = railPreferences.preferredOrigin;
      }
      if ("preferredDestination" in railPreferences) {
        payload.preferredDestination = railPreferences.preferredDestination;
      }
      if ("classType" in railPreferences) {
        payload.classType = railPreferences.classType;
      }
      if ("ticketOption" in railPreferences) {
        payload.ticketOption = railPreferences.ticketOption;
      }
      if (railPreferences.seating?.position || railPreferences.seating?.type) {
        payload.seating = {
          position: railPreferences.seating.position || (payload.seating?.position ?? ""),
          type: railPreferences.seating.type || (payload.seating?.type ?? "") || DEFAULT_SEATING_TYPE,
        };
      }
      if ("meal" in railPreferences) {
        payload.meal = railPreferences.meal;
      }

      if ("favoriteDeparture" in railPreferences && railPreferences.favoriteDeparture !== undefined) {
        payload.favoriteDeparture = railPreferences.favoriteDeparture;
      }

      // Limit 2 favorites for each type
      const MAX_FAVORITE_LOCATIONS = 2;
      let favoriteDepartures = railPreferences.favoriteDepartures || userData?.railPreferences?.favoriteDepartures || []
      let favoriteDeparturesOrigin = (favoriteDepartures?.filter(loc => "origin" in loc) || []).slice(0, MAX_FAVORITE_LOCATIONS);
      let favoriteDeparturesDestination = (favoriteDepartures?.filter(loc => "destination" in loc) || []).slice(0, MAX_FAVORITE_LOCATIONS);
      payload.favoriteDepartures = [...favoriteDeparturesOrigin, ...favoriteDeparturesDestination];


      if ("preferredTrips" in railPreferences && 
          railPreferences.preferredTrips !== undefined && 
          Array.isArray(railPreferences.preferredTrips) &&
          railPreferences.preferredTrips.length > 0 &&
          railPreferences.preferredTrips[0]) {
        const trip = railPreferences.preferredTrips[0];
        if (trip.origin || trip.destination) {
          payload.preferredTrips = [{
            origin: trip.origin || "",
            destination: trip.destination || ""
          }] as any;
        }
      }

      if (!userData?.railPreferences?.multiBooking?.preferredOrigin && payload?.preferredOrigin) {
        payload.multiBooking = {
          ...(userData?.railPreferences?.multiBooking || {}),
          preferredOrigin: payload.preferredOrigin
        }
      }
      if (!userData?.railPreferences?.multiBooking?.preferredDestination && payload?.preferredDestination) {
        payload.multiBooking = {
          ...(userData?.railPreferences?.multiBooking || {}),
          preferredDestination: payload.preferredDestination
        }
      }

      const response = await updateCustomerById(userData.id.toString(), {
        railPreferences: payload
      });

      if (response.status === "ok" && response.answer) {
        updateRecoilState("userDataAtom", response.answer);
        invalidateUserCaches();
        showNotification("success", getTranslatedMessage("personaliseExpUpdated"), "", 3, "success");
      } else {
        console.warn("Failed to save personalized experience.");
      }
    } catch (error) {
      console.log("Error saving personalized experience:", error);
      throw error;
    }
  }
}

export const savePersonalizeExperienceAction = new SavePersonalizeExperienceAction();
